/**
 * Pack planning (pure — unit tested): which file parts a pack stores on this device.
 *
 *  - every sheet: the 's' bundle (instant layout + connector jumps) + the device-tier bundle
 *  - every audio file: the original
 *  - lyrics: nothing to download (inline in the manifest / persisted song query)
 *
 * Members only ever get their own audience (the manifest is already filtered). Editors see every
 * file; unless they tick "allar raddir" a pack keeps, per kind, only files aimed at their own section
 * or the whole choir — or every file of that kind when none is (a song whose sheets or tracks are all
 * aimed at other voices keeps them). This is the viewer's rule too (viewer/fileChoice.ts
 * ownAudienceOrAll), so every sheet and track the song page lists offline is stored.
 *
 * Order (= download order): all 's' bundles first (every song readable soon), then device-tier
 * bundles, then audio with the user's own section first, then whole-choir, then other voices.
 */
import { audienceSpecificity, roleAtLeast, type Role, type Section } from '../../shared/audience';
import { SHEET_TIER_WIDTH, type SheetTier } from '../../shared/dto/library';
import { mediaUrl, type ManifestDTO, type ManifestFile } from '../../shared/dto/offline';
import { ownAudienceOrAll } from '../viewer/fileChoice';
import type { PackKind, PlannedItem } from './types';

export interface PlanUser {
  role: Role;
  section: Section | null;
}

export interface PackSpec {
  kind: PackKind;
  refId: string | null;
  allSections: boolean;
}

/** Large tier this device needs: smallest of m/l whose width ≥ longest screen side × DPR × 0.9 (else l). */
export function deviceTier(screenW: number, screenH: number, dpr: number): 'm' | 'l' {
  const need = Math.max(screenW || 0, screenH || 0) * Math.max(1, dpr || 1) * 0.9;
  return SHEET_TIER_WIDTH.m >= need ? 'm' : 'l';
}

/** Song ids of a pack (deduplicated, pack order), or null when its target no longer exists. */
export function packSongIds(spec: Pick<PackSpec, 'kind' | 'refId'>, manifest: ManifestDTO): string[] | null {
  if (spec.kind === 'all') return Object.keys(manifest.songs);
  if (!spec.refId) return null;
  if (spec.kind === 'song') return manifest.songs[spec.refId] ? [spec.refId] : null;
  const list = manifest.collections[spec.refId];
  if (!list) return null;
  return [...new Set(list.filter((id) => manifest.songs[id]))];
}

/**
 * Bundles to store for one sheet: 's' + the device tier (closest available tier when missing).
 * Devices that need 'l' (iPads in landscape fit-width) also keep 'm': portrait and two-page mode
 * use it, and decoding 'l' there would exhaust the decode budget (no pre-decoded neighbours or
 * connector targets). Costs ≈ 35–40 % more storage per sheet on those devices.
 */
export function sheetTiers(file: Pick<ManifestFile, 'parts'>, tier: SheetTier): SheetTier[] {
  const out: SheetTier[] = [];
  if (file.parts.s) out.push('s');
  if (tier === 's') return out;
  const larger: SheetTier[] = tier === 'm' ? ['m', 'l'] : ['l', 'm'];
  const pick = larger.find((t) => file.parts[t]);
  if (pick) out.push(pick);
  if (pick === 'l' && file.parts.m) out.push('m');
  return out;
}

/** Files of one song this pack keeps (see module comment for the editor rule). */
export function selectSongFiles(files: readonly ManifestFile[], user: PlanUser, allSections: boolean): ManifestFile[] {
  const editor = roleAtLeast(user.role, 'editor');
  if (!editor || allSections) return [...files];
  const keep = new Set<ManifestFile>([
    ...ownAudienceOrAll(files, 'sheet', user.section),
    ...ownAudienceOrAll(files, 'audio', user.section),
    ...ownAudienceOrAll(files, 'lyrics', user.section),
  ]);
  return files.filter((f) => keep.has(f));
}

function item(file: ManifestFile, part: 's' | 'm' | 'l' | 'orig'): PlannedItem | null {
  const p = file.parts[part];
  if (!p) return null;
  return { url: mediaUrl(file.id, file.rev, part), fileId: file.id, songId: file.songId, rev: file.rev, part, size: p.size, etag: p.etag, mime: p.mime };
}

/** Planned items for a set of songs, in download order, deduplicated by URL. */
export function planSongs(songIds: readonly string[], manifest: ManifestDTO, user: PlanUser, tier: SheetTier, allSections: boolean): PlannedItem[] {
  const byId = new Map(manifest.files.map((f) => [f.id, f]));
  const small: PlannedItem[] = [];
  const large: PlannedItem[] = [];
  const audio: { it: PlannedItem; spec: number; seq: number }[] = [];
  let seq = 0;
  for (const songId of songIds) {
    const files = (manifest.songs[songId] ?? []).map((id) => byId.get(id)).filter((f): f is ManifestFile => !!f);
    for (const f of selectSongFiles(files, user, allSections)) {
      if (f.kind === 'sheet') {
        for (const t of sheetTiers(f, tier)) {
          const it = item(f, t);
          if (it) (t === 's' ? small : large).push(it);
        }
      } else if (f.kind === 'audio') {
        const it = item(f, 'orig');
        if (it) audio.push({ it, spec: audienceSpecificity(f.audienceMask, user.section), seq: seq++ });
      }
    }
  }
  audio.sort((a, b) => b.spec - a.spec || a.seq - b.seq);
  const seen = new Set<string>();
  const out: PlannedItem[] = [];
  for (const it of [...small, ...large, ...audio.map((a) => a.it)]) {
    if (seen.has(it.url)) continue;
    seen.add(it.url);
    out.push(it);
  }
  return out;
}

/** Planned items of a pack, or null when the pack's song/collection is gone from the manifest. */
export function planPack(spec: PackSpec, manifest: ManifestDTO, user: PlanUser, tier: SheetTier): PlannedItem[] | null {
  const songs = packSongIds(spec, manifest);
  if (!songs) return null;
  return planSongs(songs, manifest, user, tier, spec.allSections);
}

export function plannedBytes(items: readonly PlannedItem[]): number {
  let n = 0;
  for (const it of items) n += it.size;
  return n;
}
